import { SpecifiedByList } from '~/ui-rocco/pages/detail-shared/SpecifiedByList';
import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    DetailCreditsRow,
    type DetailCreditPair,
} from '~/ui-rocco/pages/detail-shared';
import { PagesEnum } from '~/utils/route-config';
import { useProductVariantColumns } from './ProductVariantColumns';
import { useProductDetailItems } from './spec-accordion/use-product-detail-items';

/**
 * Where a tag pill goes: the products directory with the tag as its search
 * term, as the project's tags go to theirs.
 */
const tagSearchLink = (tag: string) =>
    `${PagesEnum.PRODUCTS}?search=${encodeURIComponent(tag)}`;

/**
 * The Product Details column: what the product is, where it sits and who
 * sells it, then every fact with a single answer — the ones the list beside
 * the picture no longer carries.
 */
export const useProductDetailPairs = (): DetailCreditPair[] => {
    const { product } = useLoaderData<typeof loader>();
    const { facts } = useProductDetailItems();
    const category = facts.find((fact) => fact.label === 'Category');
    const professional = product.Professional;

    const pairs: DetailCreditPair[] = [
        { label: 'Product', value: product.Title },
        ...(category
            ? [
                  {
                      label: category.label,
                      value: category.value,
                      link: category.link ?? undefined,
                  },
              ]
            : []),
        ...(professional?.Title
            ? [
                  {
                      label: 'Sold by',
                      value: professional.Title,
                      link: professional.Link ?? undefined,
                  },
              ]
            : []),
        ...facts
            .filter((fact) => fact !== category)
            .map((fact) => ({
                label: fact.label,
                value: fact.value,
                link: fact.link ?? undefined,
            })),
    ];
    return pairs.filter((pair) => pair.value);
};

/**
 * The project page's meta row, on the product, from left to right: the
 * search tags as pills, Product Details, then what decides the variant —
 * colours as swatches, materials and sizes as names — each a pick that
 * opens the drawer (see `useProductVariantColumns`) — and who specifies it.
 * The variants start on the edge the copy beside the picture below starts
 * on; with the specifiers beside them they stack in one column.
 */
export const ProductCreditsRow = ({
    showDetails = true,
    showVariants = true,
    showSpecifiers = true,
    hideMobileTagsLabel = false,
}: {
    /** Who specifies the product, last in the row. */
    showSpecifiers?: boolean;
    /**
     * The variant columns (colours, materials, sizes). Mobile picks variants
     * in the purchase block at the top of the page, so there they would
     * repeat.
     */
    showVariants?: boolean;
    /** Set the tag pills without their label below md. */
    hideMobileTagsLabel?: boolean;
    /**
     * The Product Details column. Mobile carries it inside the description
     * instead, behind that block's Read more, and keeps only the tags here.
     */
    showDetails?: boolean;
}) => {
    const { product, specifiers } = useLoaderData<typeof loader>();
    const variantColumns = useProductVariantColumns();
    const pairs = useProductDetailPairs();

    const tags = (product.SearchTags ?? [])
        .map((tag) => tag?.Text)
        .filter((text): text is string => !!text)
        .map((label) => ({ label, to: tagSearchLink(label) }));

    return (
        <DetailCreditsRow
            tags={tags}
            hideMobileTagsLabel={hideMobileTagsLabel}
            columns={
                showDetails
                    ? [
                          {
                              label: 'Product Details',
                              entries: [],
                              pairs,
                              pairsAsTable: true,
                          },
                      ]
                    : []
            }
            alignedColumns={showVariants ? variantColumns : []}
            alignedLabel="Product Variants"
            trailingColumn={
                showSpecifiers && specifiers?.specifiers.length
                    ? {
                          // Named for what the list is: the product's own
                          // specifiers, or its supplier's where the product
                          // is in no project yet.
                          label: specifiers.byProjects
                              ? 'Product specified by'
                              : `${product.Professional.Title} is specified by`,
                          entries: [],
                          node: (
                              <SpecifiedByList
                                  specifiers={specifiers.specifiers}
                                  moreTo={
                                      specifiers.byProjects
                                          ? undefined
                                          : `/professional/${product.Professional.URLSegment}/partners`
                                  }
                              />
                          ),
                      }
                    : undefined
            }
        />
    );
};
